import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage";

function EleveTuteur(props) {
  const { eleve, onEleveUpdated } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTuteurId, setSelectedTuteurId] = useState("");
  const [allProfs, setProfs] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/professeurs")
      .then((res) => {
        setProfs(res.data || []);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    setSelectedTuteurId(eleve?.professeurId ? String(eleve.professeurId) : "");
  }, [eleve?.professeurId]);

  const tuteursDisponibles = useMemo(() => {
    return allProfs.filter((prof) => {
      return prof.role === "Tuteur" || prof.role === "Encadrant et Tuteur";
    });
  }, [allProfs]);

  const tuteurActuel = useMemo(() => {
    return tuteursDisponibles.find(
      (prof) => Number.parseInt(prof.id, 10) === Number.parseInt(eleve?.professeurId, 10)
    );
  }, [eleve?.professeurId, tuteursDisponibles]);

  if (!eleve) {
    return null;
  }

  const applyUpdate = async (nextProfesseurId, successMessage) => {
    setFeedbackMessage("");
    setErrorMessage("");
    setSubmitting(true);

    try {
      const response = await axiosInstance.put(`/eleves/${eleve.id}`, {
        nom: eleve.nom,
        prenom: eleve.prenom,
        email: eleve.email,
        numero_tel: eleve.numero_tel,
        numero_tel_parent: eleve.numero_tel_parent,
        adress: eleve.adress,
        etablissement: eleve.etablissement,
        professeurId: nextProfesseurId,
        parcoursId: eleve.parcoursId,
      });

      setFeedbackMessage(successMessage);

      if (onEleveUpdated) {
        onEleveUpdated(response.data?.eleveUpd || { ...eleve, professeurId: nextProfesseurId });
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        extractApiErrorMessage(
          error,
          "La mise a jour du tuteur n'a pas pu etre finalisee."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedTuteurId) {
      setErrorMessage("Choisissez d'abord un tuteur a attribuer.");
      setFeedbackMessage("");
      return;
    }

    const shouldContinue = window.confirm(
      "Voulez-vous vraiment changer le tuteur de cet eleve ?"
    );

    if (!shouldContinue) {
      return;
    }

    await applyUpdate(
      Number.parseInt(selectedTuteurId, 10),
      "Le tuteur de cet eleve a bien ete mis a jour."
    );
  };

  const handleRemoveTutor = async () => {
    const shouldContinue = window.confirm(
      "Voulez-vous vraiment retirer le tuteur de cet eleve ?"
    );

    if (!shouldContinue) {
      return;
    }

    await applyUpdate(
      null,
      "Le tuteur de cet eleve a bien ete retire."
    );
  };

  return (
    <div className="eleve-management-block">
      <div className="eleve-management-header">
        <div>
          <p className="eleve-management-tag">Gestion tuteur</p>
          <h2>Tuteur de l'eleve</h2>
          <p className="eleve-management-text">
            {tuteurActuel
              ? `${tuteurActuel.prenom} ${tuteurActuel.nom} (${tuteurActuel.email})`
              : "Aucun tuteur n'est encore attribue a cet eleve."}
          </p>
        </div>
        <button className="btn" type="button" onClick={() => setIsOpen((value) => !value)}>
          {isOpen ? "Fermer" : "Changer le tuteur"}
        </button>
      </div>

      {isOpen && (
        <form className="eleve-management-form" onSubmit={handleSubmit}>
          <label htmlFor="eleve-tuteur-select">
            Choisir un tuteur disponible :
          </label>
          <select
            id="eleve-tuteur-select"
            value={selectedTuteurId}
            onChange={(event) => setSelectedTuteurId(event.target.value)}
          >
            <option value="">Selectionnez un tuteur</option>
            {tuteursDisponibles.map((prof) => (
              <option key={prof.id} value={prof.id}>
                {prof.prenom} {prof.nom} - {prof.role}
              </option>
            ))}
          </select>

          <div className="convention-actions-row">
            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Mise a jour..." : "Confirmer le tuteur"}
            </button>
            {eleve.professeurId && (
              <button
                className="btn btn-secondary"
                type="button"
                disabled={submitting}
                onClick={handleRemoveTutor}
              >
                Retirer le tuteur
              </button>
            )}
          </div>
        </form>
      )}

      {feedbackMessage && (
        <p className="eleve-feedback is-success">{feedbackMessage}</p>
      )}

      {errorMessage && (
        <p className="eleve-feedback is-error">{errorMessage}</p>
      )}
    </div>
  );
}

export default EleveTuteur;
